import React from 'react';
import { isEqual, isUndefined, uniqueId } from 'lodash-es';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

import { CONFIGURATOR_ENGAGEMENT_STEP, CONFIGURATOR_TRAFFIC_STEP } from '../configurator/constants';
import { CardsStack } from './components/CardsStack';
import { Highlight } from './types';
import { wait } from './utils';

import './styles.scss';

interface HighlightsProperties {}

interface HighlightsProps {
  children: React.ReactNode;
  id: string;
  nodeId: string;
  parentNodeId: string;
  refId: string;
  refType: string;
  uniqueId: string;
  styleText: string;
  className: string;
  properties?: HighlightsProperties;
}

function getHighlights(dataElementContext: any): Highlight[] {
  if (dataElementContext?.step?.id === CONFIGURATOR_ENGAGEMENT_STEP.id) {
    const { engagementItem, engagementHighlights } = dataElementContext ?? {};

    if (!isUndefined(engagementItem) && !isUndefined(engagementHighlights)) {
      return (engagementHighlights as Highlight[]).map((highlight, index) => ({
        ...highlight,
        id: [engagementItem.id, index].join('_'),
      }));
    }
  }

  if (dataElementContext?.step?.id === CONFIGURATOR_TRAFFIC_STEP.id) {
    const { trafficPlan, trafficHighlights } = dataElementContext ?? {};

    if (!isUndefined(trafficPlan) && !isUndefined(trafficHighlights)) {
      return (trafficHighlights as Highlight[]).map((highlight, index) => ({
        ...highlight,
        id: [trafficPlan.id, index].join('_'),
      }));
    }
  }

  return [];
}

const Highlights: React.FC<HighlightsProps> = (componentProps) => {
  let props = componentProps;
  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);
  const [uid] = React.useState(uniqueId('unique-'));

  const rawHighlights = getHighlights(dataElementContext);
  const prevHighlightsRef = React.useRef(rawHighlights);

  const highlights = React.useMemo<Highlight[]>(() => {
    if (!isEqual(rawHighlights, prevHighlightsRef.current)) {
      prevHighlightsRef.current = rawHighlights;
    }

    return prevHighlightsRef.current;
  }, [rawHighlights]);

  const [activeCardId, setActiveCardId] = React.useState<string | undefined>();
  const [completedCards, setCompletedCards] = React.useState<Set<string>>(new Set());
  const [isAutoSequencing, setIsAutoSequencing] = React.useState<boolean>(false);

  const abortControllerRef = React.useRef<AbortController | null>(null);
  const animResolveRef = React.useRef<(() => void) | null>(null);
  const mountedRef = React.useRef<boolean>(true);
  const completedCardsRef = React.useRef<Set<string>>(completedCards);
  completedCardsRef.current = completedCards;

  React.useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      // If unmounted mid-animation, consider the animation completed
      if (animResolveRef.current) {
        animResolveRef.current();
        animResolveRef.current = null;
      }

      abortControllerRef.current?.abort();
    };
  }, []);

  const markCompleted = React.useCallback((cardId: string) => {
    setCompletedCards((prev) => {
      const next = new Set(prev);
      next.add(cardId);
      return next;
    });
  }, []);

  const handleAnimationComplete = React.useCallback(
    (cardId: string) => {
      markCompleted(cardId);
      if (animResolveRef.current) {
        animResolveRef.current();
        animResolveRef.current = null;
      }
    },
    [markCompleted],
  );

  const openCardAsync = React.useCallback(
    (cardId: string): Promise<void> => {
      // Abort any in-progress animation
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const data = highlights.find((highlight) => highlight.id === cardId);

      if (isUndefined(data)) {
        return Promise.resolve();
      }

      setActiveCardId(cardId);

      // If already completed, resolve immediately
      if (completedCardsRef.current.has(cardId)) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        animResolveRef.current = resolve;
        controller.signal.addEventListener('abort', () => resolve(), { once: true });
      });
    },
    [highlights],
  );

  const closeCardInternal = React.useCallback((_cardId: string) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setActiveCardId(undefined);
  }, []);

  // Auto-sequence on mount
  React.useEffect(() => {
    if (highlights.length === 0) {
      return;
    }

    let cancelled = false;

    const hasAnyCompleted = highlights.some((h) => completedCardsRef.current.has(h.id));

    if (hasAnyCompleted) {
      // If we are returning to a list that is partially or fully completed,
      // fast-forward the entire list safely to completed so it doesn't animate.
      setCompletedCards((prev) => {
        const next = new Set(prev);
        highlights.forEach((h) => next.add(h.id));
        completedCardsRef.current = next;
        return next;
      });
      setActiveCardId(highlights[0].id);
      setIsAutoSequencing(false);
      return;
    }

    setActiveCardId(undefined);

    const runSequence = async () => {
      setIsAutoSequencing(true);

      for (let i = 0; i < highlights.length; i++) {
        if (cancelled) return;

        const data = highlights[i];

        // Close previous card
        if (i > 0) {
          closeCardInternal(highlights[i - 1].id);
        }

        if (cancelled) return;

        await openCardAsync(data.id);

        if (cancelled) return;

        // Pause to read content
        await wait(1725);
      }

      if (cancelled) return;

      // Close last card
      closeCardInternal(highlights[highlights.length - 1].id);

      if (cancelled) return;

      // Reopen first card (instant — already completed)
      const firstData = highlights[0];
      setActiveCardId(firstData.id);

      setIsAutoSequencing(false);
    };

    runSequence();

    return () => {
      cancelled = true;
    };
  }, [highlights, closeCardInternal, openCardAsync]);

  // Handle manual card clicks
  const handleCardClick = React.useCallback(
    (cardId: string) => {
      if (isAutoSequencing) return;

      if (activeCardId === cardId) {
        // Close current
        closeCardInternal(cardId);
      } else {
        // Close all, open clicked
        if (activeCardId) {
          closeCardInternal(activeCardId);
        }
        openCardAsync(cardId);
      }
    },
    [isAutoSequencing, activeCardId, closeCardInternal, openCardAsync],
  );

  return (
    <CardsStack
      cardsData={highlights}
      activeCardId={activeCardId}
      completedCards={completedCards}
      isAutoSequencing={isAutoSequencing}
      abortSignal={abortControllerRef.current?.signal}
      onCardClick={handleCardClick}
      onAnimationComplete={handleAnimationComplete}
    />
  );
};

export default Highlights;
